<template>
  <div
    class="modal fade"
    tabindex="-1"
    id="modal_reprint_prescription"
    data-bs-keyboard="false"
    data-bs-backdrop="static"
  >
    <div class="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
      <div class="modal-content">
        <div class="modal-header">
          <div class="d-flex flex-column">
            <h5 class="modal-title">Reimprimir prescrição</h5>
            <small v-if="patient" class="text-muted">{{ patient.patient_name }}</small>
          </div>

          <div
            class="btn btn-icon btn-sm btn-active-light-primary ms-2"
            @click="$emit('onCloseModal')"
            data-bs-dismiss="modal"
            aria-label="Fechar"
          >
            <i class="bi bi-x-lg"></i>
          </div>
        </div>

        <div class="modal-body">
          <div v-if="loading" class="text-center py-10">
            Carregando prescrições...
          </div>

          <div v-else>
            <div v-if="prescriptions.length === 0" class="text-center py-10 text-muted">
              Nenhuma prescrição encontrada para este paciente.
            </div>

            <div v-else class="table-responsive">
              <table class="table table-hover table-row-bordered align-middle">
                <thead>
                  <tr class="fw-bold fs-6 text-muted">
                    <th style="min-width: 180px;">Data/Hora</th>
                    <th style="min-width: 200px;">Profissional</th>
                    <th style="min-width: 200px;">Agendamento/Atendimento</th>
                    <th style="min-width: 160px;">Tipo</th>
                    <th class="text-center" style="min-width: 160px;">Status</th>
                    <th class="text-center" style="min-width: 140px;">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="p in prescriptions" :key="p.id_presc">
                    <td>
                      <div class="d-flex flex-column">
                        <span class="fw-semibold">{{ formatDateTime(p.created_at ?? p.created_at_formatted ?? p.datetime) }}</span>
                        <small class="text-muted">#{{ p.id_presc }}</small>
                      </div>
                    </td>
                    <td>{{ p.professional_name ?? p.prof_name ?? '-' }}</td>
                    <td>
                      <div class="d-flex flex-column">
                        <span>{{ p.schedule_label ?? p.attendance_label ?? p.service_label ?? '-' }}</span>
                        <small v-if="p.schedule_datetime || p.attendance_datetime" class="text-muted">
                          {{ formatDateTime(p.schedule_datetime ?? p.attendance_datetime) }}
                        </small>
                      </div>
                    </td>
                    <td>{{ p.presc_type_name ?? p.prescription_type?.presc_type_name ?? '-' }}</td>
                    <td class="text-center">
                      <span v-if="p.presc_request_signed" class="badge badge-light-success">
                        <i class="bi bi-pen-fill me-1"></i> Assinado
                      </span>
                      <span v-else-if="p.presc_request_generated" class="badge badge-light-primary">
                        <i class="bi bi-file-pdf-fill me-1"></i> Gerado
                      </span>
                      <span v-else class="badge badge-light-warning">
                        <i class="bi bi-hourglass-split me-1"></i> Pendente
                      </span>
                    </td>
                    <td class="text-center">
                      <button
                        class="btn btn-sm btn-icon btn-bg-light btn-active-color-primary border border-gray-400"
                        type="button"
                        title="Reimprimir"
                        :disabled="regeneratingId === p.id_presc"
                        @click="startReprint(p)"
                      >
                        <i class="bi bi-printer"></i>
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <PdfViewer
    :id="'modal_pdf_viewer_reprint_prescription'"
    :pdf-source="pdfSource"
    :file-name="fileName"
    :signed="signed"
    :key="'PdfReprint:' + (patient?.id_patient ?? 'none')"
  />

  <SelectCertifier
    :id="'modal_certifier_reprint_prescription'"
    :certifiers="certifiers"
    :key="'CertifierReprint:' + (patient?.id_patient ?? 'none')"
    @onSelect="printPrescription"
  />
</template>

<script>
import http from "../../helpers/http";
import Alert from "../../helpers/alert";
import PdfViewer from "../attendance/shared/PdfViewer.vue";
import SelectCertifier from "../../components/SelectCertificator.vue";

export default {
  name: "ModalReprintPrescription",
  components: { PdfViewer, SelectCertifier },
  props: ["patient", "openModal"],
  emits: ["onCloseModal"],
  data() {
    return {
      loading: false,
      prescriptions: [],
      certifiers: [],
      regeneratingId: null,
      selectedOriginalPrescription: null,
      selectedNewPrescriptionId: null,
      pdfSource: "",
      fileName: "",
      signed: false,
    };
  },
  computed: {
    place() {
      return this.$store.state.auth.place;
    },
    userCertifier() {
      return this.$store.state.auth.certifier ?? null;
    },
  },
  mounted() {
    if (this.patient != null && this.openModal) {
      this.toggleModal();
    }
  },
  watch: {
    openModal(newValue) {
      if (newValue) {
        this.toggleModal();
      }
    },
    patient(newValue) {
      if (!newValue) {
        this.prescriptions = [];
      }
    },
  },
  methods: {
    date: moment,
    toggleModal() {
      const modal = bootstrap.Modal.getOrCreateInstance(document.querySelector("#modal_reprint_prescription"));
      modal.toggle();

      if (this.patient?.id_patient) {
        this.loadCertifiers();
        this.loadPrescriptions();
      }
    },
    loadCertifiers() {
      http
        .get(`/digital-certificates/certifiers`)
        .then((response) => {
          this.certifiers = response.data ?? [];
        })
        .catch(() => {
          this.certifiers = [];
        });
    },
    formatDateTime(value) {
      if (!value) {
        return "-";
      }

      try {
        return this.date(value).format("DD/MM/YYYY HH:mm");
      } catch (e) {
        return value;
      }
    },
    loadPrescriptions() {
      if (!this.patient?.id_patient) {
        return;
      }

      this.loading = true;
      this.prescriptions = [];

      http
        .get(`/places/${this.place.id_place}/patients/${this.patient.id_patient}/prescriptions`)
        .then((response) => {
          this.prescriptions = response.data?.data ?? response.data ?? [];
        })
        .catch((e) => {
          Alert.toastError(e.response?.data?.message ?? e.response?.data ?? "Erro ao carregar prescrições.");
        })
        .finally(() => {
          this.loading = false;
        });
    },
    startReprint(prescription) {
      if (!prescription?.id_presc) {
        return;
      }

      this.selectedOriginalPrescription = prescription;
      this.regeneratingId = prescription.id_presc;

      http
        .post(
          `/places/${this.place.id_place}/patients/${this.patient.id_patient}/prescriptions/${prescription.id_presc}/regenerate`,
          {}
        )
        .then((response) => {
          const newPrescId =
            response.data?.new_presc_id ??
            response.data?.newPrescId ??
            response.data?.id_presc ??
            response.data?.data?.id_presc ??
            null;

          if (!newPrescId) {
            Alert.toastError("Não foi possível regenerar a prescrição.");
            return;
          }

          this.selectedNewPrescriptionId = newPrescId;
          this.showSelectCertifierOrPrint();
        })
        .catch((e) => {
          if (e.response?.status === 422) {
            Alert.toastError("Não foi possível reimprimir: existe medicamento sem nome na prescrição.");
            return;
          }

          Alert.toastError(e.response?.data?.message ?? e.response?.data ?? "Erro ao regenerar prescrição.");
        })
        .finally(() => {
          this.regeneratingId = null;
        });
    },
    showSelectCertifierOrPrint() {
      // Regra: se não houver certifier/assinatura no usuário, permitir imprimir sem assinatura
      if (!this.userCertifier || this.certifiers.length === 0) {
        this.printPrescription(null);
        return;
      }

      const modal = bootstrap.Modal.getOrCreateInstance(
        document.getElementById("modal_certifier_reprint_prescription")
      );
      modal.toggle();
    },
    printPrescription(certifier) {
      const newPrescId = this.selectedNewPrescriptionId;
      if (!newPrescId) {
        return;
      }

      http
        .post(
          `/places/${this.place.id_place}/patients/${this.patient.id_patient}/prescriptions/${newPrescId}/print`,
          { certifier_id: certifier?.id ?? null }
        )
        .then(async (response) => {
          const data = response.data ?? {};
          const filename = data.authenticator?.file?.file_name ?? data.file_name ?? `prescricao_${newPrescId}.pdf`;

          const fileBase64 = data.file_base64 ?? data.base64 ?? null;
          const prescSigned = data.prescription?.prescription_signed ?? data.prescription?.presc_request_signed ?? false;

          if (fileBase64 && (prescSigned || certifier?.id === null || certifier?.id === "none" || !certifier)) {
            this.showPdfViewer(`data:application/pdf;base64,${fileBase64}`, filename, !!prescSigned);
            return;
          }

          if (certifier?.id === "vidaas") {
            await this.signCloud(newPrescId, certifier, filename);
            return;
          }

          Alert.toastError("Não foi possível concluir a impressão.");
        })
        .catch((e) => {
          Alert.toastError(e.response?.data?.message ?? e.response?.data ?? "Erro ao gerar/baixar prescrição.");
        });
    },
    async signCloud(prescId, certifier, filename) {
      try {
        const response = await http.post(`${certifier.url}/prescription/${prescId}`);
        const data = response.data ?? {};
        const signedBase64 = data.base64_signed ?? data.file_base64 ?? null;

        if (!signedBase64) {
          Alert.toastError("Erro ao assinar documento.");
          return;
        }

        this.showPdfViewer(`data:application/pdf;base64,${signedBase64}`, filename, true);
      } catch (e) {
        Alert.toastError("Erro ao assinar documento.");
      }
    },
    showPdfViewer(pdfSource, fileName, signed) {
      this.pdfSource = pdfSource;
      this.fileName = fileName;
      this.signed = signed;

      const modal = bootstrap.Modal.getOrCreateInstance(document.getElementById("modal_pdf_viewer_reprint_prescription"));
      modal.toggle();
    },
  },
};
</script>

<style scoped></style>

